import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, RouterButtonLink } from "../components/Button";
import { Card } from "../components/Card";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { Container } from "../components/Container";
import { FormActions, FormActionsStart } from "../components/FormActions";
import { InfoBox } from "../components/InfoBox";
import { LoadingContainer } from "../components/LoadingContainer";
import { PageDetailHeader } from "../components/PageDetailHeader";
import { useCatalogScope } from "../hooks/useCatalogScope";
import { CATALOG_NEW_ITEM_ID, useCatalogNav } from "./catalog/catalogNav";
import { UnitForm } from "./components/UnitForm";
import {
  useGetUnitQuery,
  useCreateUnitMutation,
  useUpdateUnitMutation,
  useDeleteUnitMutation,
} from "../query/unitsQuery";
import type { RestCreateUnit } from "@KassaroAB/shared/types/unit";
import type { UnitId } from "@KassaroAB/shared/types/ids";

const FORM_ID = "catalog-unit-form";

export const UnitFormView = () => {
  const { t } = useTranslation();
  const { setDirty, allowNavigate } = useCatalogNav();
  const { scope, pagePath } = useCatalogScope("units");
  const { itemId } = useParams<{ itemId: string }>();
  const isCreating = itemId === CATALOG_NEW_ITEM_ID;
  const unitId = !isCreating && itemId ? (itemId as UnitId) : null;

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [savedTick, setSavedTick] = useState(0);

  const {
    data: unit,
    isLoading,
    error: loadError,
  } = useGetUnitQuery(scope, unitId);
  const {
    mutate: createMutation,
    error: createError,
    isPending: isCreatePending,
  } = useCreateUnitMutation(scope);
  const {
    mutate: updateMutation,
    error: updateError,
    isPending: isUpdatePending,
  } = useUpdateUnitMutation(scope);
  const {
    mutate: deleteMutation,
    error: deleteError,
    isPending: isDeletePending,
  } = useDeleteUnitMutation(scope);

  const isSaving = isCreatePending || isUpdatePending;
  const missing = !isCreating && !!unitId && !isLoading && !unit;

  const handleSubmit = (data: RestCreateUnit) => {
    if (isCreating) {
      createMutation(data, {
        onSuccess: (created) => {
          allowNavigate(`${pagePath}/${created.id}`);
        },
      });
      return;
    }
    if (!unitId) return;
    updateMutation(
      { unitId, unit: data },
      {
        onSuccess: () => {
          setSuccessMessage(t("unitsView.successUpdated"));
          setSavedTick((tick) => tick + 1);
        },
        onError: () => setSuccessMessage(null),
      },
    );
  };

  const confirmDelete = () => {
    if (!unitId) return;
    deleteMutation(unitId, {
      onSuccess: () => {
        setDeleteOpen(false);
        allowNavigate(pagePath);
      },
    });
  };

  useEffect(() => {
    if (!successMessage) return;
    const timer = setTimeout(() => setSuccessMessage(null), 3000);
    return () => clearTimeout(timer);
  }, [successMessage]);

  const title = isCreating
    ? t("unitsView.panelCreate")
    : (unit?.name ?? t("unitsView.title"));
  const breadcrumbCurrent = isCreating
    ? t("unitsView.panelCreate")
    : missing
      ? t("catalog.itemNotFound")
      : (unit?.name ?? t("common.loading"));

  if (!scope) {
    return null;
  }

  const showForm = isCreating || (!!unit && !missing);

  return (
    <Container>
      <PageDetailHeader
        title={title}
        parentLabel={t("unitsView.title")}
        parentTo={pagePath}
        currentLabel={breadcrumbCurrent}
      />
      {loadError && (
        <InfoBox variant="error">{t("unitsView.errorLoad")}</InfoBox>
      )}
      {(createError || updateError || deleteError) && (
        <InfoBox variant="error">{t("unitsView.errorSave")}</InfoBox>
      )}
      {successMessage && (
        <InfoBox variant="success">{successMessage}</InfoBox>
      )}
      <Card>
        {!isCreating && isLoading && !unit ? (
          <LoadingContainer message={t("unitsView.loading")} />
        ) : missing || !showForm ? (
          <InfoBox variant="error">{t("catalog.itemNotFound")}</InfoBox>
        ) : (
          <>
            <UnitForm
              key={unit?.id ?? CATALOG_NEW_ITEM_ID}
              formId={FORM_ID}
              unit={isCreating ? undefined : unit}
              savedTick={savedTick}
              onSubmit={handleSubmit}
              onDirtyChange={setDirty}
            />
            <FormActions>
              {!isCreating && (
                <FormActionsStart>
                  <Button
                    type="button"
                    $color="danger"
                    onClick={() => setDeleteOpen(true)}
                    disabled={isSaving || isDeletePending}
                  >
                    {t("common.delete")}
                  </Button>
                </FormActionsStart>
              )}
              <RouterButtonLink
                to={pagePath}
                $color="secondary"
                onClick={(event) => {
                  if (isSaving) event.preventDefault();
                }}
              >
                {t("common.cancel")}
              </RouterButtonLink>
              <Button type="submit" form={FORM_ID} disabled={isSaving}>
                {isSaving
                  ? t("common.saving")
                  : isCreating
                    ? t("common.create")
                    : t("common.update")}
              </Button>
            </FormActions>
          </>
        )}
      </Card>
      <ConfirmDialog
        isOpen={deleteOpen}
        title={t("unitForm.deleteUnit")}
        message={t("unitsView.deleteConfirm", {
          name: unit?.name ?? "",
        })}
        confirmLabel={t("common.delete")}
        cancelLabel={t("common.cancel")}
        danger
        onConfirm={confirmDelete}
        onCancel={() => setDeleteOpen(false)}
      />
    </Container>
  );
};
